import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, RotateCcw, Info, Check, ChevronLeft, Music } from 'lucide-react';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';
import { haptic } from '@/utils/haptic';
import { WallpapersResponse } from '@/lib/collections/wallpapers';
import { UsersTrashResponse, deleteUsersTrash, deleteUsersPurchases, getManyUsersPurchases } from '@/lib/collections/users';

// 30 days in seconds
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60; // 2592000

type ResolvedMediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

function resolveMediaType(w: WallpapersResponse): ResolvedMediaType {
  const stored = (w as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'wallpaper' || stored === 'live-wallpaper' || stored === 'ringtone' || stored === 'icon') {
    return stored;
  }
  const url = (w.imageUrl || '').toLowerCase().split('?')[0];
  if (/\.(mp4|webm|mov|m4v)$/.test(url)) return 'live-wallpaper';
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(url)) return 'ringtone';
  return 'wallpaper';
}

function getDaysRemaining(deletedAt: number): number {
  const secondsElapsed = Math.floor(Date.now() / 1000) - deletedAt;
  const secondsRemaining = THIRTY_DAYS_SECONDS - secondsElapsed;
  return Math.max(0, Math.ceil(secondsRemaining / (24 * 60 * 60)));
}

interface TrashRingtoneRowProps {
  wallpaper: WallpapersResponse;
  trashItem: UsersTrashResponse | undefined;
  isPlaying: boolean;
  isSelected: boolean;
  selectionMode: boolean;
  onPlayPause: (id: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleSelect: (id: string) => void;
}

const TrashRingtoneRow: React.FC<TrashRingtoneRowProps> = ({
  wallpaper, trashItem, isPlaying, isSelected, selectionMode,
  onPlayPause, onRestore, onDelete, onToggleSelect,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const audioUrl = wallpaper.imageUrl ?? '';
  const daysLeft = trashItem ? getDaysRemaining(trashItem.deletedAt) : 30;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setCurrentTime(audio.currentTime);
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration);
  };

  const handleEnded = () => {
    setProgress(0);
    setCurrentTime(0);
    if (isPlaying) onPlayPause(wallpaper.id);
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      onClick={() => { if (selectionMode) onToggleSelect(wallpaper.id); }}
      style={{ marginBottom: 8, width: '100%', cursor: selectionMode ? 'pointer' : 'default' }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata"
        onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />

      {selectionMode ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: isSelected ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${isSelected ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: 16, padding: '12px 14px',
          transition: 'border-color 0.2s, background 0.2s',
          minHeight: 72, width: '100%', boxSizing: 'border-box',
        }}>
          <div style={{
            flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
            background: isSelected ? 'rgba(139,92,246,0.9)' : 'rgba(0,0,0,0.5)',
            border: isSelected ? '2px solid rgba(167,139,250,1)' : '2px solid rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isSelected && <Check size={14} color="#fff" strokeWidth={3} />}
          </div>
          {/* Audio icon thumbnail */}
          <div style={{
            width: 44, height: 44, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(139,92,246,0.25) 0%, rgba(59,7,100,0.4) 100%)',
            border: '1px solid rgba(139,92,246,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Music size={18} color="#a78bfa" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#f0f0f5', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "'Syne', sans-serif" }}>
              {wallpaper.title}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(240,240,245,0.4)', margin: '2px 0 0' }}>
              Ringtone{duration > 0 ? ` • ${formatDuration(duration)}` : ''}
            </p>
            <p style={{ fontSize: 11, color: '#fca5a5', margin: '2px 0 0', fontWeight: 600 }}>
              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </p>
          </div>
        </div>
      ) : (
        /* Normal mode: card with thumbnail + info + action buttons */
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16, padding: '10px 12px',
          minHeight: 80, width: '100%', boxSizing: 'border-box',
        }}>
          {/* Audio icon thumbnail (smaller, centered in same-size card slot) */}
          <div style={{
            width: 76, height: 76, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(59,7,100,0.5) 100%)',
              border: '1px solid rgba(139,92,246,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Music size={20} color="#a78bfa" />
            </div>
          </div>
          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontSize: 14, fontWeight: 700, color: '#f0f0f5', margin: 0,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              fontFamily: "'Syne', sans-serif",
            }}>
              {wallpaper.title}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(240,240,245,0.4)', margin: '3px 0 0' }}>
              Ringtone{duration > 0 ? ` • ${formatDuration(duration)}` : ''}
            </p>
            <p style={{ fontSize: 11, color: '#fca5a5', margin: '3px 0 0', fontWeight: 600 }}>
              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </p>
          </div>
          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <motion.button
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={(e) => { e.stopPropagation(); haptic.light(); onRestore(wallpaper.id); }}
              title="Restore"
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'transparent',
                border: '1px solid rgba(94,234,212,0.45)',
                color: '#5eead4', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                touchAction: 'manipulation',
              }}
            >
              <RotateCcw size={14} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={(e) => { e.stopPropagation(); haptic.medium(); onDelete(wallpaper.id); }}
              title="Delete permanently"
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'transparent',
                border: '1px solid rgba(252,165,165,0.45)',
                color: '#fca5a5', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                touchAction: 'manipulation',
              }}
            >
              <X size={14} />
            </motion.button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

interface TrashViewProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | undefined;
  trashItems: UsersTrashResponse[];
  trashedWallpapers: WallpapersResponse[];
  onRestore: (wallpaperId: string) => Promise<void>;
  onDeletePermanently: (wallpaperId: string) => Promise<void>;
}

export const TrashView: React.FC<TrashViewProps> = ({
  isOpen,
  onClose,
  userId,
  trashItems,
  trashedWallpapers,
  onRestore,
  onDeletePermanently,
}) => {
  const cleanupRanRef = useRef(false);
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [isEmptyingTrash, setIsEmptyingTrash] = useState(false);
  const [singleDeleteDialogOpen, setSingleDeleteDialogOpen] = useState(false);
  const [wallpaperToDelete, setWallpaperToDelete] = useState<string | null>(null);
  const [isDeletingSingle, setIsDeletingSingle] = useState(false);

  // Multi-select state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [bulkAction, setBulkAction] = useState<'restore' | 'delete' | null>(null);

  // Ringtone playback state
  const [playingRingtoneId, setPlayingRingtoneId] = useState<string | null>(null);

  // Auto-cleanup expired items (older than 30 days) when Trash view opens
  useEffect(() => {
    if (!isOpen || !userId || cleanupRanRef.current) return;

    const cleanupExpiredItems = async () => {
      const currentTimeSeconds = Math.floor(Date.now() / 1000);
      const expiredItems = trashItems.filter(
        item => currentTimeSeconds - item.deletedAt > THIRTY_DAYS_SECONDS
      );

      if (expiredItems.length === 0) {
        cleanupRanRef.current = true;
        return;
      }

      for (const item of expiredItems) {
        await deleteUsersTrash(userId, item.wallpaperId);
      }

      cleanupRanRef.current = true;
    };

    cleanupExpiredItems();
  }, [isOpen, userId, trashItems]);

  // Reset cleanup flag when trash view closes
  useEffect(() => {
    if (!isOpen) {
      cleanupRanRef.current = false;
    }
  }, [isOpen]);

  const getTrashItem = (wallpaperId: string) =>
    trashItems.find(t => t.wallpaperId === wallpaperId);

  // Multi-select handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedIds(new Set());
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === trashedWallpapers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(trashedWallpapers.map(w => w.id)));
    }
  };

  const toggleSelection = (wallpaperId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(wallpaperId)) {
      newSelected.delete(wallpaperId);
    } else {
      newSelected.add(wallpaperId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessingBulk(true);
    setBulkAction('restore');
    for (const wallpaperId of selectedIds) {
      try { await onRestore(wallpaperId); } catch { /* handled by parent */ }
    }
    setIsProcessingBulk(false);
    setBulkAction(null);
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsProcessingBulk(true);
    setBulkAction('delete');
    for (const wallpaperId of selectedIds) {
      try { await onDeletePermanently(wallpaperId); } catch { /* handled by parent */ }
    }
    setIsProcessingBulk(false);
    setBulkAction(null);
    setBulkDeleteDialogOpen(false);
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const openBulkDeleteDialog = () => setBulkDeleteDialogOpen(true);

  const openSingleDeleteDialog = (wallpaperId: string) => {
    setWallpaperToDelete(wallpaperId);
    setSingleDeleteDialogOpen(true);
  };

  const handleSingleDelete = async () => {
    if (!wallpaperToDelete) return;
    setIsDeletingSingle(true);
    try {
      await onDeletePermanently(wallpaperToDelete);
    } finally {
      setIsDeletingSingle(false);
      setSingleDeleteDialogOpen(false);
      setWallpaperToDelete(null);
    }
  };

  // Reset selection state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectionMode(false);
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const handleEmptyTrash = async () => {
    if (!userId || trashItems.length === 0) return;
    setIsEmptyingTrash(true);
    let successCount = 0;

    for (const item of trashItems) {
      try {
        const purchases = await getManyUsersPurchases(userId, `where wallpaperId = '${item.wallpaperId}'`);
        if (purchases && purchases.length > 0) {
          for (const purchase of purchases) {
            await deleteUsersPurchases(userId, purchase.id);
          }
        }
      } catch {
        continue;
      }
      const trashDeleted = await deleteUsersTrash(userId, item.wallpaperId);
      if (trashDeleted) successCount++;
    }

    setIsEmptyingTrash(false);
    setEmptyTrashDialogOpen(false);

    if (successCount === 0) {
      haptic.error();
      notify.error('Failed to empty trash', userId);
    } else {
      haptic.success();
    }
  };

  const ringtoneWallpapers = trashedWallpapers.filter(w => resolveMediaType(w) === 'ringtone');
  const nonRingtoneWallpapers = trashedWallpapers.filter(w => resolveMediaType(w) !== 'ringtone');

  const confirmDialogStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 340,
    background: '#141420',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    touchAction: 'manipulation',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 100,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    touchAction: 'manipulation',
    backdropFilter: 'blur(6px)',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            background: '#0d0b14',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ── */}
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'rgba(13,11,20,0.97)',
            backdropFilter: 'blur(20px)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            {/* Left: back arrow */}
            <button
              onClick={onClose}
              aria-label="Back"
              style={{
                width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(240,240,245,0.7)',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <ChevronLeft size={24} strokeWidth={2} />
            </button>

            {/* Left-aligned title */}
            <h2 style={{
              flex: 1,
              fontSize: 19,
              fontWeight: 800,
              margin: 0,
              fontFamily: "'Syne', sans-serif",
              textAlign: 'left',
              letterSpacing: '-0.01em',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <span style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                backgroundImage: 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
              }}>
                Trash
              </span>
            </h2>

            {/* Right: select + empty trash buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {trashedWallpapers.length > 0 && !selectionMode && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleSelectionMode}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: 'rgba(167,139,250,0.12)',
                    border: '1px solid rgba(167,139,250,0.3)',
                    color: '#a78bfa',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "'Syne', sans-serif",
                  }}
                >
                  Select
                </motion.button>
              )}
              {selectionMode && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleSelectionMode}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(240,240,245,0.65)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "'Syne', sans-serif",
                  }}
                >
                  Cancel
                </motion.button>
              )}
              {!selectionMode && trashItems.length > 0 && (
                <motion.button
                  whileTap={{ scale: 0.92 }}
                  onClick={() => { haptic.medium(); setEmptyTrashDialogOpen(true); }}
                  title="Empty trash"
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'transparent',
                    border: '1px solid rgba(252,165,165,0.35)',
                    color: '#fca5a5',
                    cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    touchAction: 'manipulation',
                  }}
                >
                  <Trash2 size={16} />
                </motion.button>
              )}
            </div>
          </div>

          {/* Select All bar (only in selection mode) */}
          <AnimatePresence>
            {selectionMode && trashedWallpapers.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  padding: '8px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{ fontSize: 13, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif" }}>
                    {selectedIds.size} selected
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={toggleSelectAll}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 7,
                      background: selectedIds.size === trashedWallpapers.length
                        ? 'rgba(167,139,250,0.15)'
                        : 'rgba(255,255,255,0.06)',
                      border: selectedIds.size === trashedWallpapers.length
                        ? '1px solid rgba(167,139,250,0.35)'
                        : '1px solid rgba(255,255,255,0.12)',
                      color: selectedIds.size === trashedWallpapers.length ? '#a78bfa' : 'rgba(240,240,245,0.6)',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "'Syne', sans-serif",
                    }}
                  >
                    {selectedIds.size === trashedWallpapers.length ? 'Deselect All' : 'Select All'}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Scrollable Content ── */}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 14px 20px', minHeight: 0 }}>

            {/* Info banner */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '13px 14px',
              marginBottom: 14,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}>
              {/* Purple circle icon */}
              <div style={{
                flexShrink: 0,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(139,92,246,0.2)',
                border: '1px solid rgba(167,139,250,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Info size={15} color="#a78bfa" />
              </div>
              <div>
                <p style={{ fontSize: 13, color: '#f0f0f5', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>
                  Items in trash will be permanently deleted after 30 days.
                </p>
                <p style={{ fontSize: 12, color: 'rgba(240,240,245,0.4)', margin: '3px 0 0', lineHeight: 1.4 }}>
                  Auto-cleanup is enabled.
                </p>
              </div>
            </div>

            {/* Empty state */}
            {trashedWallpapers.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '80px 24px',
                  gap: 16,
                  textAlign: 'center',
                }}
              >
                <div style={{
                  width: 68, height: 68, borderRadius: '50%',
                  background: 'rgba(167,139,250,0.08)',
                  border: '1px solid rgba(167,139,250,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Trash2 size={30} color="#a78bfa" />
                </div>
                <div>
                  <p style={{ color: '#f0f0f5', fontSize: 16, fontWeight: 700, margin: '0 0 4px', fontFamily: "'Syne', sans-serif" }}>
                    Trash is empty
                  </p>
                  <p style={{ color: 'rgba(240,240,245,0.35)', fontSize: 13, margin: 0 }}>
                    Deleted items will appear here
                  </p>
                </div>
              </motion.div>
            ) : (
              <div style={{ paddingBottom: selectionMode && selectedIds.size > 0 ? 100 : 0 }}>

                {/* Ringtone rows */}
                {ringtoneWallpapers.length > 0 && (
                  <div style={{ marginBottom: nonRingtoneWallpapers.length > 0 ? 8 : 0 }}>
                    {ringtoneWallpapers.map((wallpaper) => {
                      const trashItem = getTrashItem(wallpaper.id);
                      return (
                        <TrashRingtoneRow
                          key={wallpaper.id}
                          wallpaper={wallpaper}
                          trashItem={trashItem}
                          isPlaying={playingRingtoneId === wallpaper.id}
                          isSelected={selectedIds.has(wallpaper.id)}
                          selectionMode={selectionMode}
                          onPlayPause={(id) => setPlayingRingtoneId(prev => (prev === id ? null : id))}
                          onRestore={onRestore}
                          onDelete={openSingleDeleteDialog}
                          onToggleSelect={toggleSelection}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Wallpaper list rows (horizontal card layout matching screenshot) */}
                {nonRingtoneWallpapers.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <AnimatePresence>
                      {nonRingtoneWallpapers.map((wallpaper, idx) => {
                        const trashItem = getTrashItem(wallpaper.id);
                        const isSelected = selectedIds.has(wallpaper.id);
                        const daysLeft = trashItem ? getDaysRemaining(trashItem.deletedAt) : 30;
                        const mediaType = resolveMediaType(wallpaper);
                        const typeLabel = mediaType === 'live-wallpaper' ? 'Live Wallpaper' : mediaType === 'icon' ? 'Icon' : 'Wallpaper';

                        return (
                          <motion.div
                            key={wallpaper.id}
                            layout
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0, transition: { delay: idx * 0.04 } }}
                            exit={{ opacity: 0, scale: 0.97 }}
                            onClick={() => { if (selectionMode) toggleSelection(wallpaper.id); }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '10px 12px',
                              borderRadius: 16,
                              background: isSelected
                                ? 'rgba(139,92,246,0.08)'
                                : 'rgba(255,255,255,0.03)',
                              border: `1px solid ${isSelected ? 'rgba(167,139,250,0.45)' : 'rgba(255,255,255,0.07)'}`,
                              cursor: selectionMode ? 'pointer' : 'default',
                              transition: 'border-color 0.18s, background 0.18s',
                              boxSizing: 'border-box',
                            }}
                          >
                            {/* Selection checkbox (selection mode) */}
                            {selectionMode && (
                              <div style={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                                background: isSelected ? 'rgba(139,92,246,0.9)' : 'rgba(0,0,0,0.5)',
                                border: isSelected ? '2px solid rgba(167,139,250,1)' : '2px solid rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {isSelected && <Check size={13} color="#fff" strokeWidth={3} />}
                              </div>
                            )}

                            {/* Square thumbnail */}
                            <div style={{
                              width: 76, height: 76, borderRadius: 12, flexShrink: 0,
                              overflow: 'hidden',
                              border: '1px solid rgba(255,255,255,0.08)',
                              opacity: selectionMode && !isSelected ? 0.45 : 1,
                              transition: 'opacity 0.2s',
                            }}>
                              <img
                                src={wallpaper.thumbnailUrl || wallpaper.imageUrl}
                                alt={wallpaper.title}
                                loading="lazy"
                                decoding="async"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            </div>

                            {/* Text info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#f0f0f5',
                                margin: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontFamily: "'Syne', sans-serif",
                              }}>
                                {wallpaper.title}
                              </p>
                              <p style={{
                                fontSize: 12,
                                color: 'rgba(240,240,245,0.4)',
                                margin: '4px 0 0',
                              }}>
                                {typeLabel}
                              </p>
                              <p style={{
                                fontSize: 11,
                                color: '#fca5a5',
                                margin: '4px 0 0',
                                fontWeight: 600,
                              }}>
                                {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                              </p>
                            </div>

                            {/* Action buttons (hidden in selection mode) */}
                            {!selectionMode && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                                <motion.button
                                  whileHover={{ scale: 1.08 }}
                                  whileTap={{ scale: 0.92 }}
                                  onClick={(e) => { e.stopPropagation(); haptic.light(); onRestore(wallpaper.id); }}
                                  title="Restore"
                                  style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    background: 'transparent',
                                    border: '1px solid rgba(94,234,212,0.45)',
                                    color: '#5eead4',
                                    cursor: 'pointer', padding: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    touchAction: 'manipulation',
                                  }}
                                >
                                  <RotateCcw size={14} />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.08 }}
                                  whileTap={{ scale: 0.92 }}
                                  onClick={(e) => { e.stopPropagation(); haptic.medium(); openSingleDeleteDialog(wallpaper.id); }}
                                  title="Delete permanently"
                                  style={{
                                    width: 32, height: 32, borderRadius: 8,
                                    background: 'transparent',
                                    border: '1px solid rgba(252,165,165,0.45)',
                                    color: '#fca5a5',
                                    cursor: 'pointer', padding: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    touchAction: 'manipulation',
                                  }}
                                >
                                  <X size={14} />
                                </motion.button>
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom safe area */}
          <div style={{ height: 'env(safe-area-inset-bottom, 16px)' }} />

          {/* ── Confirmation overlays ── */}

          {/* Empty trash overlay */}
          <AnimatePresence>
            {emptyTrashDialogOpen && (
              <motion.div
                key="empty-trash-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setEmptyTrashDialogOpen(false)}
                style={overlayStyle}
              >
                <motion.div
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.92, opacity: 0 }}
                  transition={{ type: 'spring', damping: 26, stiffness: 360 }}
                  onClick={(e) => e.stopPropagation()}
                  style={confirmDialogStyle}
                >
                  <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: '0 0 6px', fontFamily: "'Syne', sans-serif" }}>
                      Empty trash?
                    </h3>
                    <p style={{ fontSize: 13, color: 'rgba(240,240,245,0.5)', margin: 0, lineHeight: 1.6 }}>
                      This will permanently delete all {trashItems.length} item{trashItems.length !== 1 ? 's' : ''}. This can&apos;t be undone.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setEmptyTrashDialogOpen(false)}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(240,240,245,0.7)', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        fontFamily: "'Syne', sans-serif", touchAction: 'manipulation',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { haptic.medium(); handleEmptyTrash(); }}
                      disabled={isEmptyingTrash}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(252,165,165,0.15)', border: '1px solid rgba(252,165,165,0.35)',
                        color: '#fca5a5', cursor: isEmptyingTrash ? 'wait' : 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        opacity: isEmptyingTrash ? 0.65 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        touchAction: 'manipulation',
                      }}
                    >
                      {isEmptyingTrash ? (
                        <>
                          <span style={{
                            width: 13, height: 13, borderRadius: '50%',
                            border: '2px solid rgba(252,165,165,0.3)', borderTopColor: '#fca5a5',
                            animation: 'spin 0.7s linear infinite', display: 'inline-block',
                          }} />
                          Deleting...
                        </>
                      ) : (
                        <><Trash2 size={13} /> Delete All</>
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Single delete overlay */}
          <AnimatePresence>
            {singleDeleteDialogOpen && (
              <motion.div
                key="single-delete-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { if (!isDeletingSingle) { setSingleDeleteDialogOpen(false); setWallpaperToDelete(null); } }}
                style={overlayStyle}
              >
                <motion.div
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.92, opacity: 0 }}
                  transition={{ type: 'spring', damping: 26, stiffness: 360 }}
                  onClick={(e) => e.stopPropagation()}
                  style={confirmDialogStyle}
                >
                  <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: '0 0 6px', fontFamily: "'Syne', sans-serif" }}>
                      Delete permanently?
                    </h3>
                    <p style={{ fontSize: 13, color: 'rgba(240,240,245,0.5)', margin: 0, lineHeight: 1.6 }}>
                      This item will be removed forever. This can&apos;t be undone.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => { setSingleDeleteDialogOpen(false); setWallpaperToDelete(null); }}
                      disabled={isDeletingSingle}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(240,240,245,0.7)', cursor: isDeletingSingle ? 'wait' : 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        opacity: isDeletingSingle ? 0.5 : 1, touchAction: 'manipulation',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { haptic.medium(); handleSingleDelete(); }}
                      disabled={isDeletingSingle}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(252,165,165,0.15)', border: '1px solid rgba(252,165,165,0.35)',
                        color: '#fca5a5', cursor: isDeletingSingle ? 'wait' : 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        opacity: isDeletingSingle ? 0.65 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        touchAction: 'manipulation',
                      }}
                    >
                      {isDeletingSingle ? (
                        <>
                          <span style={{
                            width: 13, height: 13, borderRadius: '50%',
                            border: '2px solid rgba(252,165,165,0.3)', borderTopColor: '#fca5a5',
                            animation: 'spin 0.7s linear infinite', display: 'inline-block',
                          }} />
                          Deleting...
                        </>
                      ) : (
                        <><X size={13} /> Delete</>
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bulk delete overlay */}
          <AnimatePresence>
            {bulkDeleteDialogOpen && (
              <motion.div
                key="bulk-delete-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { if (!isProcessingBulk) setBulkDeleteDialogOpen(false); }}
                style={overlayStyle}
              >
                <motion.div
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.92, opacity: 0 }}
                  transition={{ type: 'spring', damping: 26, stiffness: 360 }}
                  onClick={(e) => e.stopPropagation()}
                  style={confirmDialogStyle}
                >
                  <div>
                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: '0 0 6px', fontFamily: "'Syne', sans-serif" }}>
                      Delete {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}?
                    </h3>
                    <p style={{ fontSize: 13, color: 'rgba(240,240,245,0.5)', margin: 0, lineHeight: 1.6 }}>
                      {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} will be permanently deleted. This can&apos;t be undone.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setBulkDeleteDialogOpen(false)}
                      disabled={isProcessingBulk}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(240,240,245,0.7)', cursor: isProcessingBulk ? 'wait' : 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        opacity: isProcessingBulk ? 0.5 : 1, touchAction: 'manipulation',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { haptic.medium(); handleBulkDelete(); }}
                      disabled={isProcessingBulk && bulkAction === 'delete'}
                      style={{
                        flex: 1, padding: '11px 16px', borderRadius: 12,
                        background: 'rgba(252,165,165,0.15)', border: '1px solid rgba(252,165,165,0.35)',
                        color: '#fca5a5',
                        cursor: (isProcessingBulk && bulkAction === 'delete') ? 'wait' : 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                        opacity: (isProcessingBulk && bulkAction === 'delete') ? 0.65 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        touchAction: 'manipulation',
                      }}
                    >
                      {isProcessingBulk && bulkAction === 'delete' ? (
                        <>
                          <span style={{
                            width: 13, height: 13, borderRadius: '50%',
                            border: '2px solid rgba(252,165,165,0.3)', borderTopColor: '#fca5a5',
                            animation: 'spin 0.7s linear infinite', display: 'inline-block',
                          }} />
                          Deleting...
                        </>
                      ) : (
                        <><Trash2 size={13} /> Delete</>
                      )}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Bulk Action Bar ── */}
          <AnimatePresence>
            {selectionMode && selectedIds.size > 0 && (
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                style={{
                  position: 'fixed',
                  bottom: 0, left: 0, right: 0,
                  padding: '14px 16px',
                  paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 16px))',
                  background: 'rgba(15,12,22,0.92)',
                  backdropFilter: 'blur(20px)',
                  borderTop: '1px solid rgba(255,255,255,0.07)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  zIndex: 20,
                }}
              >
                <span style={{
                  fontSize: 13, color: 'rgba(240,240,245,0.6)', fontWeight: 700,
                  fontFamily: "'Syne', sans-serif", minWidth: 80,
                }}>
                  {selectedIds.size} selected
                </span>
                <div style={{ flex: 1, display: 'flex', gap: 10 }}>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleBulkRestore}
                    disabled={isProcessingBulk}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      padding: '12px',
                      borderRadius: 12,
                      background: 'rgba(94,234,212,0.1)',
                      border: '1px solid rgba(94,234,212,0.35)',
                      color: '#5eead4',
                      cursor: isProcessingBulk ? 'wait' : 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                      opacity: isProcessingBulk ? 0.65 : 1,
                    }}
                  >
                    {isProcessingBulk && bulkAction === 'restore' ? (
                      <>
                        <span style={{
                          width: 14, height: 14, borderRadius: '50%',
                          border: '2px solid rgba(94,234,212,0.3)', borderTopColor: '#5eead4',
                          animation: 'spin 0.7s linear infinite', display: 'inline-block',
                        }} />
                        Restoring...
                      </>
                    ) : (
                      <><RotateCcw size={14} /> Restore</>
                    )}
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={openBulkDeleteDialog}
                    disabled={isProcessingBulk}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      padding: '12px',
                      borderRadius: 12,
                      background: 'rgba(252,165,165,0.1)',
                      border: '1px solid rgba(252,165,165,0.35)',
                      color: '#fca5a5',
                      cursor: isProcessingBulk ? 'wait' : 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: "'Syne', sans-serif",
                      opacity: isProcessingBulk ? 0.65 : 1,
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
